/* @flow */

import type { SearchResult } from "shop-state/types";

import React from "react";
import { Helmet } from "react-helmet-async";
import { search } from "@crossroads/shop-state/search";
import FilterableProductList from "components/FilterableProductList";
import { useData, useSendMessage } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { SearchData } from "data";
import LoadingView from "components/LoadingView";
import { useLocation } from "react-router-dom";
import styles from "./styles.scss";
import Breadcrumbs from "components/Breadcrumbs";
import { Wrapper } from "@crossroads/ui-components";

type SearchProps = {
  data: SearchResult,
  query: string,
  updating: boolean,
};

type WrapperProps = {
  query: string,
};

const SearchViewWrapper = ({ query }: WrapperProps): React$Node => {
  const search = useData(SearchData);

  if (search.state === "LOADED" || search.state === "UPDATING") {
    return (
      <SearchView
        data={search.data}
        query={query}
        updating={search.state === "UPDATING"}
      />
    );
  }

  return <LoadingView />;
};

const SearchView = ({ data, query, updating }: SearchProps) => {
  const t = useTranslate();
  const sendMessage = useSendMessage();
  const location = useLocation();

  const load = location => sendMessage(search(query, location, true));

  if (data.items.length === 0) {
    return (
      <Wrapper>
        <Breadcrumbs current={`${t("BREADCRUMBS.SEARCH_RESULTS_FOR")} "${query}"`} />

        <p>{t("SEARCH.EMPTY")}</p>
      </Wrapper>
    );
  }

  return (
    <div className={styles.block}>
      <Helmet title={`${t("SEARCH.TITLE_PREFIX")}${query}`} />

      <Wrapper>
        <Breadcrumbs current={`${t("BREADCRUMBS.SEARCH_RESULTS_FOR")} "${query}"`} />

        <h1 className="h2">
          <span>
            {data.totalCount > 1 ?
              t("SEARCH.PRODUCTS_IN_SEARCH_RESULT", { itemsCount: data.totalCount }) :
              t("SEARCH.PRODUCT_IN_SEARCH_RESULT")
            }

          </span> &quot;{query}&quot;
        </h1>
      </Wrapper>

      <FilterableProductList
        updating={updating}
        productList={data}
        load={load}
        breadcrumbLinks={[{
          pathname: location.pathname,
          search: location.search,
          title: t("SEARCH.RESULTS", { query }),
          hint: {
            name: t("SEARCH.RESULTS", { query }),
          },
        }]}
        category={`${t("SEARCH.TITLE_PREFIX")}${query}`}
      />

    </div>
  );
};

export default SearchViewWrapper;
